import { Callback } from '../../../types';
import { isMobile } from '../../../utils';
import AgoraRTC, {
  IAgoraRTCClient,
  ILocalAudioTrack,
  ILocalVideoTrack,
  IAgoraRTCRemoteUser,
} from 'agora-rtc-sdk-ng';
import { useCallback, useEffect, useRef, useState } from 'react';
import type { AgoraRoomLike, AgoraParticipantLike, AgoraPublicationLike } from '../../../types';
import { isRemoteScreenVideoTrack } from '../../../utils/agoraRemoteVideo';

const appId = process.env.REACT_APP_AGORA_APP_ID || '';

function buildParticipantFromRemote(user: IAgoraRTCRemoteUser, identity?: string): AgoraParticipantLike {
  const uid = user.uid;
  return {
    identity: identity || `User ${uid}`,
    uid: typeof uid === 'number' ? uid : parseInt(String(uid), 10) || 0,
    get audioTrack() {
      return user.audioTrack ?? null;
    },
    get videoTrack() {
      const vt = user.videoTrack ?? null;
      if (!vt) return null;
      return isRemoteScreenVideoTrack(vt) ? null : vt;
    },
    get screenTrack() {
      const vt = user.videoTrack ?? null;
      if (!vt) return null;
      return isRemoteScreenVideoTrack(vt) ? vt : null;
    },
    getPublications(): AgoraPublicationLike[] {
      const out: AgoraPublicationLike[] = [];
      if (user.audioTrack) out.push({ kind: 'audio', track: user.audioTrack, trackName: 'audio' });
      if (user.videoTrack) {
        const screen = isRemoteScreenVideoTrack(user.videoTrack);
        out.push({
          kind: 'video',
          track: user.videoTrack,
          trackName: screen ? 'screen' : 'camera',
        });
      }
      return out;
    },
  };
}

function buildLocalParticipant(
  identity: string,
  uid: number,
  localTracks: (ILocalAudioTrack | ILocalVideoTrack)[]
): AgoraParticipantLike {
  const audioTrack = localTracks.find((t): t is ILocalAudioTrack => t.trackMediaType === 'audio') ?? null;
  const videoTrack = localTracks.find(
    (t): t is ILocalVideoTrack => t.trackMediaType === 'video' && !(t as any).trackName?.includes?.('screen')
  ) ?? null;
  const screenTrack = localTracks.find(
    (t): t is ILocalVideoTrack => (t as any).trackName?.includes?.('screen')
  ) ?? null;
  return {
    identity,
    uid,
    get audioTrack() {
      return audioTrack as any;
    },
    get videoTrack() {
      return videoTrack as any;
    },
    get screenTrack() {
      return screenTrack as any;
    },
    getPublications(): AgoraPublicationLike[] {
      const out: AgoraPublicationLike[] = [];
      if (audioTrack) out.push({ kind: 'audio', track: audioTrack, trackName: 'audio' });
      if (videoTrack) out.push({ kind: 'video', track: videoTrack, trackName: 'camera' });
      if (screenTrack) out.push({ kind: 'video', track: screenTrack, trackName: 'screen' });
      return out;
    },
  };
}

export default function useRoom(
  localTracks: (ILocalAudioTrack | ILocalVideoTrack)[],
  onError: Callback,
  _options?: unknown
) {
  const [room, setRoom] = useState<AgoraRoomLike | null>(null);
  const [isConnecting, setIsConnecting] = useState(false);
  const channelNameRef = useRef<string>('');
  const localIdentityRef = useRef<string>('');
  const localUidRef = useRef<number>(0);
  const localTracksRef = useRef(localTracks);
  useEffect(() => {
    localTracksRef.current = localTracks;
  }, [localTracks]);

  const connect = useCallback(
    (token: string, channelName: string, uid: number, displayName: string) => {
      if (!appId) {
        onError(new Error('REACT_APP_AGORA_APP_ID is not set'));
        return Promise.reject(new Error('REACT_APP_AGORA_APP_ID is not set'));
      }
      setIsConnecting(true);
      channelNameRef.current = channelName;
      localIdentityRef.current = displayName;
      localUidRef.current = uid;

      const client: IAgoraRTCClient = AgoraRTC.createClient({ mode: 'rtc', codec: 'vp8' });

      return client
        .join(appId, channelName, token, uid)
        .then((joinedUid: string | number) => {
          // When joining with uid 0/null, Agora assigns an id — that value must be used for UI.
          // For Firestore disconnect we also parse `username=User-{id}` in videoCallFirestore when token uid ≠ business id.
          if (joinedUid !== undefined && joinedUid !== null && joinedUid !== '') {
            if (typeof joinedUid === 'number' && Number.isFinite(joinedUid)) {
              localUidRef.current = joinedUid;
            } else {
              const n = parseInt(String(joinedUid), 10);
              if (Number.isFinite(n)) {
                localUidRef.current = n;
              }
            }
          }
          const tracksToPublish = localTracks.filter(Boolean);
          if (tracksToPublish.length > 0) {
            return client.publish(tracksToPublish).then(() => client);
          }
          return client;
        })
        .then(() => {
          const participants = new Map<string, AgoraParticipantLike>();
          client.remoteUsers.forEach(user => {
            const p = buildParticipantFromRemote(user);
            participants.set(String(p.uid), p);
          });

          const updateParticipants = () => {
            const next = new Map<string, AgoraParticipantLike>();
            client.remoteUsers.forEach(user => {
              const p = buildParticipantFromRemote(user);
              next.set(String(p.uid), p);
            });
            setRoom(r =>
              r
                ? {
                    ...r,
                    participants: next,
                    connectionState: client.connectionState,
                    get localParticipant() {
                      return buildLocalParticipant(localIdentityRef.current, localUidRef.current, localTracksRef.current);
                    },
                  }
                : null
            );
          };

          client.on('user-published', async (user: IAgoraRTCRemoteUser, mediaType: string) => {
            await client.subscribe(user, mediaType as 'audio' | 'video');
            updateParticipants();
          });
          client.on('user-unpublished', () => updateParticipants());
          client.on('user-left', () => updateParticipants());
          client.on('connection-state-change', (state: string) => {
            setRoom(r => (r ? { ...r, connectionState: state } : null));
          });

          const leave = () => client.leave();
          const disconnect = () => client.leave();

          const roomLike: AgoraRoomLike = {
            client,
            channelName,
            get localParticipant() {
              return buildLocalParticipant(localIdentityRef.current, localUidRef.current, localTracksRef.current);
            },
            participants,
            connectionState: client.connectionState,
            leave,
            disconnect,
          };

          setRoom(roomLike);

          client.on('user-joined', () => updateParticipants());
          client.on('user-left', () => updateParticipants());

          const cleanup = () => {
            leave();
            setRoom(null);
            window.removeEventListener('beforeunload', cleanup);
            if (isMobile) window.removeEventListener('pagehide', cleanup);
          };

          window.addEventListener('beforeunload', cleanup);
          if (isMobile) window.addEventListener('pagehide', cleanup);

          setIsConnecting(false);
        })
        .catch(err => {
          onError(err);
          setIsConnecting(false);
        });
    },
    [localTracks, onError]
  );

  return { room, isConnecting, connect };
}
